/**
 * Max number of automation name characters kept in the mailbox slug.
 */
const AUTOMATION_NAME_SLUG_MAX_LENGTH = 15;

/**
 * Converts an automation name into a slug safe for an email local part.
 *
 * Accents are removed, the name is lowercased, and each run of characters
 * outside [a-z0-9] becomes a single hyphen. The result is cut to the first
 * 15 characters, with leading/trailing hyphens trimmed.
 *
 * Examples:
 * - "Café Invoices" → "cafe-invoices"
 * - "Invoice Processing Flow" → "invoice-process"
 * - "🚀🚀" / "   " / undefined → ""
 *
 * @param name - The automation name
 * @returns The slug, or an empty string if nothing usable remains
 */
export function slugifyAutomationName(name: string | undefined | null): string {
  if (!name) return '';

  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, AUTOMATION_NAME_SLUG_MAX_LENGTH)
    .replace(/-+$/, '');
}

/**
 * Generates a mailbox email address for an automation based on the current domain.
 *
 * Pattern (with automation name, used for new triggers):
 * - Draft: automation-{nameSlug}-{automationId}-draft@{environment}.{region}.kognitos.com
 * - Published: automation-{nameSlug}-{automationId}-published@{environment}.{region}.kognitos.com
 *
 * Pattern (without name, or when the name slug is empty):
 * - Draft: automation-{automationId}-draft@{environment}.{region}.kognitos.com
 * - Published: automation-{automationId}-published@{environment}.{region}.kognitos.com
 *
 * {nameSlug} is the slug of the first 15 characters of the automation name
 * (see slugifyAutomationName). Existing triggers keep their stored address;
 * this only affects newly generated addresses.
 *
 * Examples (app.us-1.dev.kognitos.com):
 * - Draft: automation-invoices-123-draft@us-1.dev.kognitos.com
 * - Published (no name): automation-123-published@us-1.dev.kognitos.com
 *
 * @param automationId - The automation ID to include in the email address
 * @param isDraft - Whether the automation is in draft stage (default true)
 * @param automationName - Optional automation name used to build the slug
 * @returns The generated mailbox email address
 */
export function generateMailboxAddress(
  automationId: string,
  isDraft = true,
  automationName?: string,
): string {
  // Get the current hostname
  const hostname = window.location.hostname;

  // Default domain for localhost development
  const DEFAULT_DOMAIN = 'us-1.dev.kognitos.com';

  let emailDomain: string;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Use default domain for local development
    emailDomain = DEFAULT_DOMAIN;
  } else if (
    hostname.startsWith('app.') &&
    hostname.endsWith('.kognitos.com')
  ) {
    // Extract the domain part after 'app.' for production/staging environments
    // e.g., app.us-1.dev.kognitos.com → us-1.dev.kognitos.com
    emailDomain = hostname.replace('app.', '');
  } else {
    // Fallback to default domain for any other cases
    emailDomain = DEFAULT_DOMAIN;
  }

  const stage = isDraft ? 'draft' : 'published';
  const nameSlug = slugifyAutomationName(automationName);

  const localPart = nameSlug
    ? `automation-${nameSlug}-${automationId}-${stage}`
    : `automation-${automationId}-${stage}`;

  return `${localPart}@${emailDomain}`;
}

/**
 * Extracts the automation ID from a mailbox email address.
 *
 * Note: this returns the whole local part after "automation-". For the
 * name-based format it therefore includes the name slug and stage suffix
 * (e.g. "invoices-123-draft"), not only the automation ID.
 *
 * @param mailboxAddress - The mailbox email address
 * @returns The automation ID or null if the format doesn't match
 */
export function extractAutomationIdFromMailbox(
  mailboxAddress: string,
): string | null {
  const match = mailboxAddress.match(/^automation-([^@]+)@/);
  return match ? match[1] : null;
}
